import { useState } from "react";
import {
  TagView,
  SnippetsForTagView,
  useDeleteTag,
  useGetAllTaggedInstances,
  useGetAllUserTags,
} from "../api-generated";
import Button from "./Button";
import ErrorLoading from "./ErrorLoading";
import CenteredLoading from "./Loading";
import Modal from "./Modal";
import Table from "./Table";
import TagForm from "./TagForm";
import { formatDateTime, getTokenListString } from "../utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import Header from "./Header";
import InterestingWordInContext from "./InterestingWordInContext";
import { TAGGED_INSTANCES_QUERY_KEY } from "../domain_data";

interface Props {
  tags: TagView[];
  instances: SnippetsForTagView[];
}
const TAGS_QUERY_KEY: QueryKey = ["TAGSBOI"];

const Tags: React.FC<Props> = ({ tags, instances }) => {
  const [beingViewedTagIndex, setBeingViewedTagIndex] = useState(-1);
  const [tagBeingEdited, setTagBeingEdited] = useState<TagView | undefined>(
    undefined
  );
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const deleteTagMut = useDeleteTag();
  const qc = useQueryClient();

  const handleTagClick = (_: TagView, tableRowIndex: number): void => {
    setBeingViewedTagIndex((prev) =>
      tableRowIndex === prev ? -1 : tableRowIndex
    );
  };

  const COLUMNS = [
    {
      columnName: "Name",
      getContent: (row: TagView) => <div>{row.name}</div>,
    },
    {
      columnName: "Created At",
      getContent: (row: TagView) => <div>{formatDateTime(row.createdAt)}</div>,
    },
    {
      columnName: "Edit name",
      getContent: (row: TagView) => (
        <div className="flex justify-start pl-4">
          <button
            className="text-blue-500"
            onClick={() => setTagBeingEdited(row)}
          >
            <FontAwesomeIcon icon={faPencil} />
          </button>
        </div>
      ),
    },
    {
      columnName: "Delete",
      getContent: (row: TagView) => (
        //SNIPPET
        <div className="flex justify-start pl-4">
          <button
            className="text-red-500"
            onClick={() =>
              deleteTagMut.mutate(
                { id: row.id },
                {
                  onSuccess: () => {
                    qc.invalidateQueries(TAGS_QUERY_KEY);
                  },
                }
              )
            }
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ),
    },
  ];

  const tagBeingViewed: TagView | undefined = tags[beingViewedTagIndex];
  const beingViewedInstances: SnippetsForTagView | undefined = instances.find(
    //TODO why doesn't typescript tell me I need a ? here?
    (instance) => instance.tag.id === tagBeingViewed?.id
  );

  return (
    <div>
      <div className="py-4">
        <Button
          label="Create New"
          type="primary"
          onClick={() => setIsCreateFormOpen(true)}
        />
      </div>
      <Modal
        isOpen={isCreateFormOpen || !!tagBeingEdited}
        onTriggerClose={() => {
          setIsCreateFormOpen(false);
          setTagBeingEdited(undefined);
        }}
      >
        <TagForm
          tag={tagBeingEdited}
          tagQueryKey={TAGS_QUERY_KEY}
          onClose={() => setIsCreateFormOpen(false)}
        />
      </Modal>

      <Table columns={COLUMNS} rows={tags} onClick={handleTagClick} />

      <div className="pt-4">
        {!!tagBeingViewed && beingViewedInstances === undefined && (
          <Header
            text={`No instances of tag: ${tagBeingViewed.name}`}
            type="main"
          />
        )}

        {tagBeingViewed === undefined && <p>No tag selected</p>}

        {beingViewedInstances && (
          <div className="flex flex-col gap-4">
            <Header
              text={`Instances of: ${beingViewedInstances.tag.name}`}
              type="main"
            />
            <div>
              {beingViewedInstances.snippetsBySource.length > 0 ? (
                <div>
                  {beingViewedInstances.snippetsBySource.map(
                    (snippetsForSource, snippetsForSourceIndex) =>
                      snippetsForSource.snippets.map(
                        (snippet, snippetIndex) => (
                          <div
                            key={`${snippetsForSourceIndex}-${snippetIndex}`}
                          >
                            <InterestingWordInContext snippet={snippet} />
                          </div>
                        )
                      )
                  )}
                </div>
              ) : (
                <p>No instances of this tag</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Wrapped: React.FC = () => {
  const taggedInstancesQuery = useGetAllTaggedInstances({
    query: { queryKey: TAGGED_INSTANCES_QUERY_KEY },
  });
  const { data, isLoading, isError, error } = useGetAllUserTags({
    query: { queryKey: TAGS_QUERY_KEY },
  });

  if (isLoading || taggedInstancesQuery.isLoading) {
    return <CenteredLoading />;
  }

  if (taggedInstancesQuery.isError) {
    return <ErrorLoading error={taggedInstancesQuery.error} />;
  }

  if (isError) {
    return <ErrorLoading error={error} />;
  }

  return <Tags tags={data} instances={taggedInstancesQuery.data} />;
};

export default Wrapped;
